import { forwardRef } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';

const tableVariants = cva(
	"w-full caption-bottom text-sm",
	{
		variants: {
			variant: {
				default: "border-collapse",
				striped: "border-collapse",
			}
		},
		defaultVariants: {
			variant: "default"
		}
	}
);

const Table = forwardRef(({ className, variant, ...props }, ref) => (
	<div className="relative w-full overflow-auto">
		<table
			ref={ref}
			className={cn(tableVariants({ variant, className }))}
			{...props}
		/>
	</div>
));
Table.displayName = "Table";

const TableHeader = forwardRef(({ className, ...props }, ref) => (
	<thead ref={ref} className={cn("border-b border-input", className)} {...props} />
));
TableHeader.displayName = "TableHeader";

const TableBody = forwardRef(({ className, variant, ...props }, ref) => (
	<tbody
		ref={ref}
		className={cn(
			variant === "striped" && "[&_tr:nth-child(odd)]:bg-muted",
			className
		)}
		{...props}
	/>
));
TableBody.displayName = "TableBody";

const TableFooter = forwardRef(({ className, ...props }, ref) => (
	<tfoot
		ref={ref}
		className={cn("border-t bg-muted/50 font-medium", className)}
		{...props}
	/>
));
TableFooter.displayName = "TableFooter";

const TableRow = forwardRef(({ className, ...props }, ref) => (
	<tr
		ref={ref}
		className={cn(
			"border-b border-border transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
			className
		)}
		{...props}
	/>
));
TableRow.displayName = "TableRow";

const TableHead = forwardRef(({ className, ...props }, ref) => (
	<th
		ref={ref}
		className={cn(
			"h-9 px-3 text-left align-middle text-xs font-medium uppercase tracking-wider text-muted-foreground [&:has([role=checkbox])]:pr-0",
			className
		)}
		{...props}
	/>
));
TableHead.displayName = "TableHead";

const TableCell = forwardRef(({ className, ...props }, ref) => (
	<td
		ref={ref}
		className={cn(
			"px-3 py-2 align-middle [&:has([role=checkbox])]:pr-0",
			className
		)}
		{...props}
	/>
));
TableCell.displayName = "TableCell";

const TableCaption = forwardRef(({ className, ...props }, ref) => (
	<caption
		ref={ref}
		className={cn("mt-3 text-sm text-muted-foreground", className)}
		{...props}
	/>
));
TableCaption.displayName = "TableCaption";

export {
	Table,
	TableHeader,
	TableBody,
	TableFooter,
	TableHead,
	TableRow,
	TableCell,
	TableCaption,
}; 